import { Link } from 'react-router-dom';
import React from 'react';
import { Search, Brain, Zap, Wrench, Settings } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';

/* ── Virtuous Cycle Graphic ─────────────────────────────────────── */

const VirtuousCycleGraphic: React.FC = () => {
  const engines = [
    {
      name: 'AutarSE',
      full: 'Scan Engine',
      desc: 'Multi-agent deep analysis of your codebase. Language, CI/CD, testing, security, docs, dependencies.',
      tag: '▶ Entry Point',
      color: '#60a5fa',
      bgColor: 'rgba(96, 165, 250, 0.15)',
      tagBg: 'rgba(96, 165, 250, 0.1)',
      tagBorder: 'rgba(96, 165, 250, 0.2)',
      icon: <Search size={20} color="#60a5fa" />,
      position: 'top' as const,
    },
    {
      name: 'AutarKG',
      full: 'Knowledge Graph',
      desc: 'Scan signals populate the graph. Maintenance outcomes enrich it. Every interaction makes it smarter.',
      tag: '📊 Intelligence',
      color: '#a78bfa',
      bgColor: 'rgba(167, 139, 250, 0.15)',
      tagBg: 'rgba(167, 139, 250, 0.1)',
      tagBorder: 'rgba(167, 139, 250, 0.2)',
      icon: <Brain size={20} color="#a78bfa" />,
      position: 'right' as const,
    },
    {
      name: 'AutarPE',
      full: 'Preference Engine',
      desc: "Queries the knowledge graph to surface the optimal config for your repo's archetype and scale.",
      tag: '🎯 Recommendations',
      color: '#34d399',
      bgColor: 'rgba(52, 211, 153, 0.15)',
      tagBg: 'rgba(52, 211, 153, 0.1)',
      tagBorder: 'rgba(52, 211, 153, 0.2)',
      icon: <Zap size={20} color="#34d399" />,
      position: 'bottom' as const,
    },
    {
      name: 'AutarMaTE',
      full: 'Maintenance Tasks Engine',
      desc: 'Executes fixes via branch, validate, reassess. Configurable trust from PR review to auto-merge.',
      tag: '🚀 Execution',
      color: '#f472b6',
      bgColor: 'rgba(244, 114, 182, 0.15)',
      tagBg: 'rgba(244, 114, 182, 0.1)',
      tagBorder: 'rgba(244, 114, 182, 0.2)',
      icon: <Wrench size={20} color="#f472b6" />,
      position: 'left' as const,
    },
  ];

  const positionStyles: Record<string, React.CSSProperties> = {
    top: { top: 0, left: '50%', transform: 'translateX(-50%)' },
    right: { top: '50%', right: -20, transform: 'translateY(-50%)' },
    bottom: { bottom: 0, left: '50%', transform: 'translateX(-50%)' },
    left: { top: '50%', left: -20, transform: 'translateY(-50%)' },
  };

  return (
    <div style={{ position: 'relative', width: 620, height: 620, margin: '0 auto' }}>
      {/* Outer ring */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 480,
          height: 480,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          border: '1.5px solid rgba(100, 116, 139, 0.2)',
        }}
      />

      {/* Animated dots */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 480,
          height: 480,
          transform: 'translate(-50%, -50%)',
          animation: 'autar-rotate 12s linear infinite',
        }}
      >
        {[
          { top: -4, left: '50%', transform: 'translateX(-50%)', bg: '#60a5fa', shadow: '#60a5fa' },
          {
            bottom: -4,
            left: '50%',
            transform: 'translateX(-50%)',
            bg: '#f472b6',
            shadow: '#f472b6',
          },
          { top: '50%', left: -4, transform: 'translateY(-50%)', bg: '#a78bfa', shadow: '#a78bfa' },
          {
            top: '50%',
            right: -4,
            transform: 'translateY(-50%)',
            bg: '#34d399',
            shadow: '#34d399',
          },
        ].map((dot, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: dot.bg,
              boxShadow: `0 0 12px ${dot.shadow}, 0 0 24px ${dot.shadow}40`,
              ...dot,
            }}
          />
        ))}
      </div>

      {/* Center circle */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 5,
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 30% 30%, #1e1b4b, #0a0a0f)',
            border: '2px solid rgba(167, 139, 250, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            boxShadow: '0 0 60px rgba(167, 139, 250, 0.1)',
          }}
        >
          <Settings size={32} color="#a78bfa" style={{ marginBottom: 4 }} />
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#a78bfa',
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            Your Repo
          </div>
          <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>Always Improving</div>
        </div>
      </div>

      {/* Engine cards */}
      {engines.map((engine) => (
        <div
          key={engine.name}
          style={{
            position: 'absolute',
            width: 220,
            padding: 20,
            borderRadius: 16,
            background: 'rgba(15, 15, 25, 0.9)',
            border: '1px solid rgba(100, 116, 139, 0.15)',
            backdropFilter: 'blur(10px)',
            zIndex: 10,
            ...positionStyles[engine.position],
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: engine.bgColor,
                flexShrink: 0,
              }}
            >
              {engine.icon}
            </div>
            <div>
              <div
                style={{ fontSize: 16, fontWeight: 700, color: engine.color, letterSpacing: -0.3 }}
              >
                {engine.name}
              </div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  opacity: 0.5,
                  color: '#fff',
                }}
              >
                {engine.full}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: '#94a3b8' }}>{engine.desc}</div>
          <div
            style={{
              display: 'inline-block',
              marginTop: 10,
              padding: '3px 10px',
              borderRadius: 20,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.5,
              background: engine.tagBg,
              color: engine.color,
              border: `1px solid ${engine.tagBorder}`,
            }}
          >
            {engine.tag}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Engine Detail Section ──────────────────────────────────────── */

interface EngineSectionProps {
  name: string;
  subtitle: string;
  color: string;
  children: React.ReactNode;
}

const EngineSection: React.FC<EngineSectionProps> = ({ name, subtitle, color, children }) => (
  <div style={{ marginBottom: 48 }}>
    <h2
      style={{
        fontSize: 28,
        fontWeight: 800,
        color,
        marginBottom: 4,
        letterSpacing: -0.5,
      }}
    >
      {name}
    </h2>
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: '#64748b',
        marginBottom: 16,
      }}
    >
      {subtitle}
    </div>
    <p style={{ fontSize: 16, lineHeight: 1.7, color: '#cbd5e1', maxWidth: 800 }}>{children}</p>
  </div>
);

/* ── Main Page ──────────────────────────────────────────────────── */

export const AutarPlatformEarlyConceptPage: React.FC = () => {
  return (
    <div
      style={{
        background: '#0a0a0f',
        minHeight: '100vh',
        color: '#ffffff',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`
        @keyframes autar-rotate {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>

      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '70px 24px 0' }}>
        <Link to="/brand" style={{ color: '#94a3b8', fontSize: 14, textDecoration: 'none' }}>
          ← Product Concepts
        </Link>
      </div>

      {/* Hero */}
      <div style={{ textAlign: 'center', paddingTop: 80, paddingBottom: 40 }}>
        <h1
          style={{
            fontSize: 42,
            fontWeight: 800,
            letterSpacing: -1,
            background: 'linear-gradient(135deg, #60a5fa, #a78bfa, #f472b6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 12,
          }}
        >
          The Autar Platform
        </h1>
        <p style={{ fontSize: 18, color: '#64748b', fontWeight: 500 }}>
          A Virtuous Cycle for Autonomous Repository Maintenance
        </p>
      </div>

      {/* Cycle Graphic */}
      <div style={{ padding: '20px 0 60px', display: 'flex', justifyContent: 'center' }}>
        <VirtuousCycleGraphic />
      </div>

      {/* Tagline */}
      <div style={{ textAlign: 'center', marginBottom: 60, padding: '0 20px' }}>
        <p style={{ fontSize: 15, color: '#475569', fontWeight: 500 }}>
          Every scan enriches the graph. Every recommendation improves. Every fix teaches the
          system.
          <br />
          <span style={{ color: '#94a3b8', fontWeight: 600 }}>
            The more you use Autar, the smarter it gets.
          </span>
        </p>
      </div>

      {/* Detailed Sections */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 80px' }}>
        <EngineSection name="AutarSE" subtitle="Scan Engine" color="#60a5fa">
          AutarSE (Scan Engine) is the entry point for every repository Autar touches. It performs a
          deep, multi-agent analysis of a codebase, deploying specialized scanners in parallel to
          evaluate language ecosystems, CI/CD pipelines, testing strategies, security posture,
          documentation quality, dependency health, and AI tooling adoption. Each scanner operates
          independently, producing structured signals that feed directly into AutarKG and AutarPE
          for pattern matching and configuration recommendations. A full scan completes in minutes,
          not hours, giving teams an immediate, comprehensive profile of their repository's current
          state, strengths, and gaps. AutarSE is designed to run on first connection and
          continuously thereafter, so the knowledge graph stays current as the codebase evolves.
          Think of it as the diagnostic that makes everything else in the Autar platform possible:
          before you can maintain, configure, or optimize, you need to deeply understand what you're
          working with.
        </EngineSection>

        <EngineSection name="AutarKG" subtitle="Knowledge Graph" color="#a78bfa">
          Autar's knowledge graph (AutarKG) is a structured map of every meaningful signal across
          thousands of analyzed repositories. The knowledge graph captures languages, frameworks,
          CI/CD patterns, test strategies, deployment targets, team size, package versions and scale
          indicators that are linked together so that when a new repo is ingested, the graph can
          pattern-match its characteristics against known archetypes and extract the optimal
          configuration. It compares patterns in similar repos that are used successfully in
          production, rather than relying on generic templates or manual setup. AutarKG applies the
          collective intelligence of the entire ecosystem into a personalized, scale-aware SDLC
          prescription for any given codebase.
        </EngineSection>

        <EngineSection name="AutarPE" subtitle="Preference Engine" color="#34d399">
          AutarPE (Preference Engine) is the intelligence layer that turns Autar's knowledge graph
          into actionable configuration decisions. By analyzing patterns across thousands of
          repositories, PE learns which tools, workflows, and settings correlate with success for
          specific repo archetypes, then surfaces those preferences as recommendations when a new
          repo is onboarded. For example, PE recognizes that a large TypeScript monorepo with 50+
          packages shares structural DNA with projects like Angular and Nx, so it recommends Bazel
          over Turborepo, strict trunk-based development over GitFlow, and integration-heavy test
          strategies over unit-only coverage. Rather than applying universal "best practices," PE
          understands that the right configuration depends on context: team size, deployment target,
          dependency graph complexity, and ecosystem norms. It is the mechanism that makes Autar's
          recommendations feel like they came from an engineer who has already solved your exact
          problem at scale.
        </EngineSection>

        <EngineSection name="AutarMaTE" subtitle="Maintenance Tasks Engine" color="#f472b6">
          AutarMaTE (Maintenance Tasks Engine) handles every maintenance action through a
          disciplined, auditable workflow: it creates a dedicated branch, pushes changes, runs the
          full validation suite, and reassesses impact before ever surfacing the result to the user.
          This process is fully customizable to fit within your existing sprint cadence, whether
          that means slotting into two-week cycles, daily deploys, or trunk-based development. Trust
          levels let you dial the automation from conservative (create a PR for human review) all
          the way to fully autonomous (validate and auto-merge when all signals pass). Every PR and
          commit is documented the way a senior engineer would do it: clear descriptions of what was
          changed, why, what was considered, and what the validation results confirmed. The goal is
          that a teammate reviewing an AutarMaTE PR can follow the reasoning as easily as if another
          developer on the team wrote it.
        </EngineSection>
      </div>

      <Footer />
    </div>
  );
};

export default AutarPlatformEarlyConceptPage;
